<template>
  <div>
    <Nav></Nav>
    <b-container fluid class="mt-3 pos-captura" :class="flashClass">
      <b-row align-v="center" class="mb-2">
        <b-col>
          <h3 class="mb-0">
            <b-icon icon="hand-index-thumb" class="mr-1"></b-icon>
            Captura · {{ sucursal.nombre || ("Sucursal " + sucursalId) }}
            <b-badge variant="warning" class="ml-2">SIMULACIÓN</b-badge>
          </h3>
          <small class="text-muted">
            Productos, grupos, precios y extras tal como los sincroniza el POS de esta sucursal.
            Nada de lo que captures aquí se guarda.
          </small>
        </b-col>
        <b-col cols="auto">
          <b-button size="sm" variant="outline-secondary" class="mr-1" @click="$router.push('/pos')">
            <b-icon icon="arrow-left"></b-icon> Cambiar sucursal
          </b-button>
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="$router.push('/pos/menu/' + sucursalId)">
            <b-icon icon="table"></b-icon> Editar menú
          </b-button>
          <b-button size="sm" variant="outline-primary" :disabled="loading" @click="loadAll">
            <b-icon icon="arrow-clockwise"></b-icon> Recargar datos
          </b-button>
        </b-col>
      </b-row>

      <b-alert :show="loadError !== ''" variant="danger" dismissible @dismissed="loadError = ''">{{ loadError }}</b-alert>

      <b-overlay :show="loading" rounded="sm">
        <!-- Cuenta simulada: equivale al "crear cuenta" del POS local; el área define los precios. -->
        <b-card no-body border-variant="primary" class="mb-2 p-2">
          <b-row align-v="end">
            <b-col md="4" sm="6">
              <b-form-group label="Área (define precios)" class="mb-1">
                <b-form-select v-model="cuenta.restaurant_area_id" :options="areaOptions" size="sm"></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="2" sm="3">
              <b-form-group label="Mesa" class="mb-1">
                <b-form-input v-model="cuenta.table_no" size="sm" placeholder="Ej. 12"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="2" sm="3">
              <b-form-group label="Personas" class="mb-1">
                <b-form-input v-model.number="cuenta.persons" type="number" min="1" size="sm"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4" class="text-md-right">
              <div class="d-flex flex-wrap justify-content-md-end" style="gap: 8px;">
                <b-badge variant="secondary" class="py-2 px-3" style="font-size: .9rem;">
                  <b-icon icon="box-seam" class="mr-1"></b-icon>{{ stats.activeProducts }} productos activos
                </b-badge>
                <b-badge variant="secondary" class="py-2 px-3" style="font-size: .9rem;">
                  <b-icon icon="tag" class="mr-1"></b-icon>{{ stats.pricesForArea }} con precio en el área
                </b-badge>
                <b-badge variant="dark" class="py-2 px-3" style="font-size: 1rem;">
                  Total: ${{ capturedPrice.toFixed(2) }}
                </b-badge>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <b-row>
          <!-- Columna izquierda: lista capturada y captura por código -->
          <b-col lg="3" md="4">
            <b-row>
              <b-col class="my-1">
                <b-button size="sm" variant="primary" @click="openPrices">
                  <b-icon icon="search"></b-icon> Consulta precio
                </b-button>
              </b-col>
            </b-row>
            <b-row>
              <b-col class="my-1">
                <b-button variant="danger" size="sm" @click="deleteProduct" :disabled="!hasSelection">Elimina producto</b-button>
                <b-button variant="warning" size="sm" @click="editProduct" :disabled="!hasSelection">Editar</b-button>
                <b-button variant="outline-secondary" size="sm" @click="clearCapture"
                  :disabled="capturingProducts.length === 0">Limpiar</b-button>
              </b-col>
            </b-row>
            <b-row>
              <b-col class="my-1">
                <b-table :items="capturingProducts" :fields="capturingProductsFields" :responsive="true"
                  select-mode="single" small selectable @row-selected="selectedRow" selected-variant="primary"
                  style="max-height: 50vh" ref="selectableTable" class="my-1 bg-white"
                  show-empty empty-text="Sin productos capturados">
                  <template #cell(price)="data">${{ Number(data.value || 0).toFixed(2) }}</template>
                  <template #cell(preparation_comment)="data">
                    <b-icon v-if="data.value" icon="check-square-fill" variant="success" :title="data.value"></b-icon>
                  </template>
                </b-table>
              </b-col>
            </b-row>
            <b-row>
              <b-col class="my-1">
                <b-input-group class="mb-1" size="sm">
                  <b-input-group-prepend is-text>Código</b-input-group-prepend>
                  <b-form-input ref="productCapture" @keydown.enter="processCaptureNew" @keydown.tab="processCaptureNew"
                    v-model="actualProduct" placeholder="clave o 2*clave" v-shortkey.focus="['alt', 'b']"></b-form-input>
                  <b-input-group-append>
                    <b-button variant="success" @click="processCaptureNew">OK</b-button>
                  </b-input-group-append>
                </b-input-group>
                <b-button-group size="sm">
                  <b-button ref="aceptaButton" variant="success" :disabled="capturingProducts.length === 0"
                    @click="captureProducts" v-shortkey="['alt', 'a']" @shortkey="captureProducts">
                    <b-icon icon="check" /> Agregar productos
                  </b-button>
                  <b-button variant="outline-dark" :disabled="capturingProducts.length === 0" @click="showPayload">
                    <b-icon icon="code-slash" /> Ver payload
                  </b-button>
                </b-button-group>
                <div class="small text-muted mt-1">Atajos: Alt+B código · Alt+A agregar · Enter en cantidad acepta.</div>
              </b-col>
            </b-row>
          </b-col>

          <!-- Columna derecha: grupos / subgrupos / productos -->
          <b-col lg="9" md="8">
            <b-row>
              <b-col>
                <b-card class="p-1">
                  Grupo:<br />
                  <b-alert :show="selectGroupOptions.length === 0 && !loading" variant="warning" class="my-1 py-1">
                    Ningún grupo tiene productos activos con precio vigente en el área seleccionada.
                  </b-alert>
                  <b-form-radio-group class="d-none d-lg-block flex-wrap" button-variant="outline-primary" size="md"
                    buttons v-model="group_id" :options="selectGroupOptions"></b-form-radio-group>
                  <b-form-select class="d-block d-lg-none" v-model="group_id" :options="selectGroupOptions"></b-form-select>
                </b-card>
              </b-col>
            </b-row>
            <b-row v-if="selectSubGroupOptions.length > 0">
              <b-col>
                <b-card class="m-1">
                  SubGrupo:<br />
                  <b-form-radio-group button-variant="primary" size="sm" buttons class="d-none d-lg-block flex-wrap"
                    v-model="subgroup_id" :options="selectSubGroupOptions"></b-form-radio-group>
                  <b-form-select class="d-block d-lg-none" v-model="subgroup_id" :options="selectSubGroupOptions"></b-form-select>
                </b-card>
              </b-col>
            </b-row>
            <b-row class="mt-3" align-v="stretch">
              <b-col v-if="filteredProducts.length === 0 && group_id" cols="12">
                <p class="text-muted">No hay productos activos con precio en esta área para el grupo/subgrupo seleccionado.</p>
              </b-col>
              <b-col xl="2" lg="2" md="3" sm="3" cols="4" class="my-1 p-1 align-items-center"
                v-for="product in filteredProducts" :key="product.id">
                <b-card img-top style="height: 100%; overflow: visible" class="mb-2 product-card" @click="qtyopen(product.clave)"
                  no-body :title="product.ndescription + ' (' + product.clave + ')'" border-variant="dark">
                  <div class="product-menu" @click.stop>
                    <b-dropdown size="sm" variant="light" no-caret right toggle-class="product-menu-toggle" boundary="window">
                      <template #button-content><b-icon icon="three-dots-vertical"></b-icon></template>
                      <b-dropdown-item @click="openProductPage(product)">
                        <b-icon icon="box-arrow-up-right" class="mr-1"></b-icon> Abrir ficha del producto
                      </b-dropdown-item>
                      <b-dropdown-item @click="openMenuEditor(product)">
                        <b-icon icon="table" class="mr-1"></b-icon> Ver en menú de la sucursal
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                  <template v-if="product.image && product.image.length > 0">
                    <b-img-lazy :src="imgSrc(product.image)" @error.native="replaceImageDefault" :blank-src="placeholder"
                      fluid></b-img-lazy>
                    <div class="mx-1 d-flex justify-content-center align-items-center w-100">
                      <div class="text-center w-100 px-2" style="word-wrap: break-word;">
                        <p class="m-0 p-0" style="font-size: .7em;">{{ product.short_name || product.ndescription }}</p>
                        <small class="text-muted">${{ priceForArea(product).toFixed(2) }}</small>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="d-flex justify-content-center align-items-center h-100" style="min-height: 5rem;">
                      <div class="text-center w-100 px-2" style="word-wrap: break-word;">
                        <p class="m-0 p-0">{{ product.short_name || product.ndescription }}</p>
                        <small class="text-muted">${{ priceForArea(product).toFixed(2) }}</small>
                      </div>
                    </div>
                  </template>
                </b-card>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-overlay>
    </b-container>

    <!-- Personalizaciones -->
    <b-modal no-fade size="xl" id="modal-productPers" title="Personalizaciones" no-close-on-backdrop no-close-on-esc
      hide-footer @keydown.native.enter="persFinishStep">
      <p v-if="actualProductCapturing.optionPersonalizations.length === 0" class="text-muted">Sin personalizaciones.</p>
      <b-row class="mb-3">
        <b-col lg="2" md="3" sm="4" cols="6" class="p-1"
          v-for="item in actualProductCapturing.optionPersonalizations" :key="item.item.personalization_id">
          <b-card no-body class="h-100 pers-card" :class="{ 'pers-selected': persIsSelected(item.item) }"
            @click="togglePersonalization(item.item)">
            <div class="pers-check">
              <b-icon :icon="persIsSelected(item.item) ? 'check-circle-fill' : 'circle'"
                :variant="persIsSelected(item.item) ? 'success' : 'secondary'" font-scale="1.3"></b-icon>
            </div>
            <b-img-lazy v-if="item.image && item.image.length > 0" :src="imgSrc(item.image)"
              @error.native="replaceImageDefault" :blank-src="placeholder" fluid class="pers-img"></b-img-lazy>
            <div class="text-center px-2 py-2" style="word-wrap: break-word;">
              <p class="m-0 p-0" style="font-size: .85em;">{{ item.text }}</p>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <b-button variant="success" @click="persFinishStep"><b-icon icon="check" /> Aceptar</b-button>
      <b-button variant="danger" @click="$bvModal.hide('modal-productPers')">Cancelar</b-button>
    </b-modal>

    <!-- Cantidad / comentario / extras -->
    <b-modal no-fade size="xl" no-close-on-backdrop no-close-on-esc id="modal-productConfig" title="Configurar producto"
      hide-footer @shown="$nextTick(() => $refs.qtyCapture && $refs.qtyCapture.focus())">
      <b-row>
        <b-col md="6">
          <b-form-group label="Cantidad">
            <b-input-group>
              <b-input-group-prepend>
                <b-button variant="outline-danger" @click="graphqty > 1 ? graphqty-- : null"><b-icon icon="dash" /></b-button>
              </b-input-group-prepend>
              <b-form-input ref="qtyCapture" type="number" v-model.number="graphqty" @keydown.native.enter="configFinish"
                placeholder="1" min="1" style="text-align: center" />
              <b-input-group-append>
                <b-button variant="outline-success" @click="graphqty = (graphqty || 1) + 1"><b-icon icon="plus" /></b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Comentario preparación">
            <b-form-input v-model="configComment" placeholder="Ej: sin cebolla, término medio..."
              @keydown.native.enter="configFinish" />
          </b-form-group>
        </b-col>
      </b-row>
      <div v-if="extrasSelection.length > 0">
        <hr />
        <div v-for="(group, gIdx) in extrasSelection" :key="gIdx" class="mb-3">
          <h5>{{ group.name }}
            <small class="text-muted">(min: {{ group.min_selection }}, max: {{ group.max_selection }})</small>
          </h5>
          <b-form-group v-if="group.max_selection === 1">
            <b-form-radio-group v-model="group.radioSelected" stacked @change="val => { group.selected = val ? [val] : [] }">
              <b-form-radio v-for="item in group.items" :key="item.id" :value="item">
                {{ item.product_name }} - ${{ Number(item.price).toFixed(2) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <div v-else>
            <div v-for="item in group.items" :key="item.id" class="d-flex align-items-center mb-2">
              <span class="mr-3" style="min-width: 200px">{{ item.product_name }} - ${{ Number(item.price).toFixed(2) }}</span>
              <b-button size="sm" variant="outline-danger" class="mr-1" :disabled="item.qty <= 0"
                @click="extraQtyChange(group, item, -1)"><b-icon icon="dash" /></b-button>
              <b-input type="number" v-model.number="item.qty" min="0" style="width: 60px; text-align: center" size="sm" readonly />
              <b-button size="sm" variant="outline-success" class="ml-1"
                :disabled="groupTotalQty(group) >= group.max_selection" @click="extraQtyChange(group, item, 1)">
                <b-icon icon="plus" /></b-button>
            </div>
          </div>
          <small v-if="groupTotalQty(group) < group.min_selection" class="text-danger">
            Selecciona al menos {{ group.min_selection }} opción(es)
          </small>
        </div>
      </div>
      <hr />
      <b-button variant="success" :disabled="!extrasValid" @click="configFinish"><b-icon icon="check" /> Aceptar</b-button>
      <b-button variant="danger" @click="configClose">Cancelar</b-button>
    </b-modal>

    <!-- Consulta de precios -->
    <b-modal no-fade id="modal-checkPrices" size="xl" title="Consulta precios" hide-footer no-close-on-backdrop>
      <b-input-group prepend="BUSCAR" class="mb-2">
        <b-input debounce="300" v-model="searchInPrices" autofocus></b-input>
      </b-input-group>
      <b-table striped hover :items="activeProducts" small :fields="priceTableCols" :filter="searchInPrices"
        selectable select-mode="single" @row-clicked="priceRowClicked" sticky-header="60vh">
        <template #cell(product_prices)="row">
          <ul class="mb-0 pl-3">
            <li v-for="value in row.item.product_prices" :key="value.id" :class="{ 'text-muted': !value.status }">
              {{ areaName(value.area_restaurant_id) }}: ${{ Number(value.price).toFixed(2) }}
              <small v-if="!value.status">(inactivo)</small>
              <small v-if="value.follow_price" class="text-muted">· sigue precio maestro</small>
            </li>
          </ul>
        </template>
      </b-table>
      <b-button variant="danger" @click="$bvModal.hide('modal-checkPrices')">Cerrar</b-button>
    </b-modal>

    <!-- Resumen: lo que el POS enviaría a /ticket/{id}/products -->
    <b-modal no-fade id="modal-resumen" size="lg" title="Resultado de la captura (simulado)" ok-only ok-title="Cerrar">
      <p class="mb-2">Así quedarían estos renglones en la cuenta de la sucursal. Aquí solo se muestran, no se guardan.</p>
      <b-table small striped :items="capturingProducts" :fields="resumenFields">
        <template #cell(product_name)="data">
          <span v-if="data.item.is_extra" class="pl-3"><b-icon icon="arrow-return-right" scale="0.8" class="mr-1" />{{ data.value }}</span>
          <strong v-else>{{ data.value }}</strong>
        </template>
        <template #cell(price)="data">${{ Number(data.value).toFixed(2) }}</template>
        <template #cell(total)="data">${{ (Number(data.item.price) * Number(data.item.quantity)).toFixed(2) }}</template>
        <template #cell(product_personalizations)="data">
          <span v-for="p in data.value" :key="p.personalization_id">
            <b-badge variant="light" class="border mr-1">{{ personalizationName(p.personalization_id) }}</b-badge>
          </span>
        </template>
      </b-table>
      <div class="text-right h4">Total: ${{ capturedPrice.toFixed(2) }}</div>
      <b-collapse id="payload-json" v-model="payloadVisible">
        <pre class="bg-light p-2 small" style="max-height: 40vh; overflow: auto;">{{ payloadJson }}</pre>
      </b-collapse>
      <b-button size="sm" variant="link" class="p-0" @click="payloadVisible = !payloadVisible">
        {{ payloadVisible ? "Ocultar" : "Ver" }} JSON
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "@/components/Nav.vue";
import { LAST_SUCURSAL_KEY } from "@/utils/posPreview";

const PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="120" viewBox="0 0 160 120"><rect width="160" height="120" fill="#e9ecef"/><text x="80" y="66" text-anchor="middle" font-family="sans-serif" font-size="14" fill="#adb5bd">sin imagen</text></svg>'
  );

export default {
  name: "PosCaptura",
  components: { Nav },
  data() {
    return {
      sucursalId: parseInt(this.$route.params.sucursal_id, 10) || 0,
      sucursal: {},
      loading: false,
      loadError: "",
      flashClass: "",
      placeholder: PLACEHOLDER,
      // Catálogos normalizados a la forma que usa el POS local.
      products: [],
      groups: [],
      areas: [],
      personalizations: [],
      cuenta: { restaurant_area_id: null, table_no: "", persons: 1 },
      // Estado de captura (espejo de CapturaProductModal en pos_local).
      group_id: null,
      subgroup_id: 1,
      selectGroupOptions: [],
      selectSubGroupOptions: [],
      filteredProducts: [],
      capturingProducts: [],
      capturingProductsFields: [
        { key: "quantity", label: "CANT" },
        { key: "product_name", label: "DESCRIPCION" },
        { key: "price", label: "IMPORTE" },
        { key: "preparation_comment", label: "" }
      ],
      selectedprod: {},
      actualProduct: "",
      graphqty: null,
      configComment: "",
      actualProductCapturing: {
        product_id: "",
        personalizations: [],
        optionPersonalizations: [],
        selectedPersonalizations: []
      },
      extrasSelection: [],
      editingExtras: null,
      // Consulta de precios
      searchInPrices: "",
      priceTableCols: [
        { key: "id", label: "ID" },
        { key: "ndescription", label: "Nombre" },
        { key: "clave", label: "Clave" },
        { key: "product_prices", label: "Precios" }
      ],
      // Resumen
      payloadVisible: false,
      resumenFields: [
        { key: "quantity", label: "Cant" },
        { key: "product_name", label: "Producto" },
        { key: "price", label: "Precio" },
        { key: "total", label: "Total" },
        { key: "preparation_comment", label: "Comentario" },
        { key: "product_personalizations", label: "Personalizaciones" }
      ]
    };
  },
  computed: {
    areaOptions() {
      return this.areas.map(a => ({ value: a.id, text: a.name }));
    },
    activeProducts() {
      return this.products.filter(p => p.status === "ACTIVE");
    },
    stats() {
      const areaId = this.cuenta.restaurant_area_id;
      return {
        activeProducts: this.activeProducts.length,
        pricesForArea: this.activeProducts.filter(p => this.validPriceForArea(p, areaId)).length
      };
    },
    capturedPrice() {
      return this.capturingProducts.reduce((total, p) => total + Number(p.price) * Number(p.quantity), 0);
    },
    hasSelection() {
      return !!this.selectedprod && Object.keys(this.selectedprod).length > 0;
    },
    extrasValid() {
      return this.extrasSelection.every(g => this.groupTotalQty(g) >= g.min_selection);
    },
    payloadJson() {
      return JSON.stringify(this.capturingProducts, null, 2);
    }
  },
  watch: {
    "cuenta.restaurant_area_id"() {
      this.rebuildGroupOptions();
    },
    group_id(newGroupId) {
      const selectedGroup = this.groups.find(g => g.id == newGroupId);
      if (!selectedGroup) {
        this.selectSubGroupOptions = [];
        this.filteredProducts = [];
        return;
      }
      this.subgroup_id = 1;
      this.selectSubGroupOptions = selectedGroup.products.length > 0 ? [{ text: "SIN G", value: 1 }] : [];
      this.selectSubGroupOptions.push(
        ...selectedGroup.product_subgroups.map(sg => ({ text: sg.button_text || sg.name, value: sg.id }))
      );
      this.updateFilteredProducts(selectedGroup.products);
    },
    subgroup_id(newSubgroupId) {
      const selectedGroup = this.groups.find(g => g.id == this.group_id);
      if (!selectedGroup) return;
      let products = [];
      if (newSubgroupId == 1) {
        products = selectedGroup.products;
      } else {
        const sg = selectedGroup.product_subgroups.find(s => s.id == newSubgroupId);
        products = sg ? sg.products : [];
      }
      this.updateFilteredProducts(products);
    }
  },
  mounted() {
    if (!this.sucursalId) {
      this.$router.replace("/pos").catch(() => {});
      return;
    }
    try {
      localStorage.setItem(LAST_SUCURSAL_KEY, String(this.sucursalId));
    } catch (e) {
      // sin localStorage no pasa nada
    }
    this.loadAll();
  },
  methods: {
    // ---------- Carga y normalización de catálogos ----------
    async loadAll() {
      this.loading = true;
      this.loadError = "";
      try {
        const [sucursal, products, prices, groups, extraGroups, areas, personalizations] = await Promise.all([
          axios.get("/sucursal/" + this.sucursalId).then(r => r.data),
          axios.get("/products").then(r => r.data || []),
          axios.get("/sucursal/" + this.sucursalId + "/products/all/prices").then(r => r.data || []),
          axios.get("/groups").then(r => r.data || []),
          axios.get("/product/extragroups").then(r => r.data || []).catch(() => []),
          axios.get("/restaurant/areas").then(r => r.data || []),
          axios.get("/personalizations").then(r => r.data || []).catch(() => [])
        ]);
        this.sucursal = sucursal || {};
        this.personalizations = personalizations;
        this.areas = areas.filter(a => !a.status || a.status === "ACTIVE");
        this.products = this.normalizeProducts(products, prices, extraGroups);
        this.groups = this.normalizeGroups(groups, this.products);

        if (!this.areas.some(a => a.id === this.cuenta.restaurant_area_id)) {
          this.cuenta.restaurant_area_id = this.areas.length > 0 ? this.areas[0].id : null;
        }
        this.rebuildGroupOptions();
      } catch (err) {
        this.loadError =
          "No se pudieron cargar los catálogos de la sucursal: " +
          ((err.response && (err.response.data.message || err.response.data)) || err.message);
      } finally {
        this.loading = false;
      }
    },
    // Replica lo que hace localback al sincronizar: /products + precios de la sucursal
    // + extras ligados por product_extra_group_products.
    normalizeProducts(products, prices, extraGroups) {
      const pricesByProduct = {};
      prices.forEach(p => {
        if (!pricesByProduct[p.product_id]) pricesByProduct[p.product_id] = [];
        pricesByProduct[p.product_id].push(p);
      });
      const extrasByProduct = {};
      extraGroups.forEach(g => {
        (g.products || []).forEach(gp => {
          if (!extrasByProduct[gp.product_id]) extrasByProduct[gp.product_id] = [];
          extrasByProduct[gp.product_id].push(g);
        });
      });
      return products.map(p => {
        const pers = p.product_personalizations || p.product_personalization || [];
        return Object.assign({}, p, {
          product_prices: pricesByProduct[p.id] || [],
          product_personalizations: pers,
          product_extra_groups: (extrasByProduct[p.id] || []).slice().sort((a, b) => a.ordenation - b.ordenation)
        });
      });
    },
    // Central devuelve los grupos con productos anidados pero sin precios; se reconstruyen
    // con los productos ya enriquecidos usando la misma regla que GetAllGroupsHandler.
    normalizeGroups(groups, products) {
      return groups.map(g => {
        const subgroups = (g.product_subgroups || []).map(sg =>
          Object.assign({}, sg, { products: products.filter(p => p.subgroup == sg.id && p.subgroup != 1) })
        );
        return Object.assign({}, g, {
          products: products.filter(p => p.group == g.id && p.subgroup == 1),
          product_subgroups: subgroups
        });
      });
    },
    validPriceForArea(product, areaId) {
      return (product.product_prices || []).find(pr => pr.area_restaurant_id == areaId && pr.status);
    },
    priceForArea(product) {
      const pr = this.validPriceForArea(product, this.cuenta.restaurant_area_id);
      return pr ? Number(pr.price) : 0;
    },
    areaName(areaId) {
      const a = this.areas.find(x => x.id == areaId);
      return a ? a.name : "Área " + areaId;
    },
    personalizationName(id) {
      const p = this.personalizations.find(x => x.id == id);
      return p ? p.name : "#" + id;
    },
    rebuildGroupOptions() {
      const areaId = this.cuenta.restaurant_area_id;
      const hasActive = products => products.some(p => p.status === "ACTIVE" && this.validPriceForArea(p, areaId));
      const hasActiveProducts = group =>
        hasActive(group.products) || group.product_subgroups.some(sg => hasActive(sg.products));

      this.selectGroupOptions = this.groups
        .filter(g => g.status && hasActiveProducts(g))
        .sort((a, b) => a.ordenation - b.ordenation)
        .map(g => ({ text: g.button_text || g.name, value: g.id }));

      const keep = this.selectGroupOptions.some(o => o.value === this.group_id);
      const next = keep ? this.group_id : this.selectGroupOptions.length > 0 ? this.selectGroupOptions[0].value : null;
      if (next === this.group_id) {
        // El watcher no se dispara si el grupo no cambió: refrescar productos por el área nueva.
        this.subgroup_id = 1;
        const g = this.groups.find(x => x.id == next);
        this.updateFilteredProducts(g ? g.products : []);
      } else {
        this.group_id = next;
      }
    },
    updateFilteredProducts(products) {
      if (!products || !products.length) {
        this.filteredProducts = [];
        return;
      }
      const areaId = this.cuenta.restaurant_area_id;
      this.filteredProducts = products
        .filter(p => p.status === "ACTIVE" && this.validPriceForArea(p, areaId))
        .sort((a, b) => (a.short_name || "").toUpperCase().localeCompare((b.short_name || "").toUpperCase()));
    },
    openProductPage(product) {
      window.open(this.$router.resolve("/product/" + product.id).href, "_blank");
    },
    openMenuEditor(product) {
      window.open(
        this.$router.resolve({ path: "/pos/menu/" + this.sucursalId, query: { q: product.clave } }).href,
        "_blank"
      );
    },
    imgSrc(path) {
      return (axios.defaults.baseURL || "") + "/" + path;
    },
    replaceImageDefault(e) {
      e.target.src = PLACEHOLDER;
    },

    // ---------- Captura (misma máquina de estados que el POS local) ----------
    qtyopen(clave) {
      this.graphqty = null;
      this.configComment = "";
      this.productToAdd(clave);
    },
    openPrices() {
      this.searchInPrices = "";
      this.$bvModal.show("modal-checkPrices");
    },
    priceRowClicked(record) {
      this.$bvModal.hide("modal-checkPrices");
      this.graphqty = null;
      this.configComment = "";
      this.productToAdd(record.clave);
    },
    processCaptureNew() {
      if (!this.actualProduct) {
        if (this.$refs.aceptaButton) this.$refs.aceptaButton.focus();
        return;
      }
      let productId;
      let quantity = 1;
      if (this.actualProduct.includes("*")) {
        const [qtyStr, prodId] = this.actualProduct.split("*");
        productId = (prodId || "").trim();
        quantity = parseFloat(qtyStr) || 1;
      } else {
        productId = this.actualProduct.trim();
      }
      this.actualProduct = "";
      this.graphqty = quantity;
      this.configComment = "";

      const product = this.products.find(p => p.clave === productId);
      if (!product) {
        this.handleError("danger", "Producto no encontrado", "No encontré el producto buscado: " + productId);
        return;
      }
      const extraGroups = (product.product_extra_groups || []).filter(g => g.status === 1 && g.items && g.items.length > 0);
      const hasRequiredExtras = extraGroups.some(g => g.min_selection >= 1);
      if (!hasRequiredExtras) {
        const defaultPers = (product.product_personalizations || [])
          .filter(p => p.default_selected)
          .map(p => ({ personalization_id: p.personalization_id }));
        this.addProduct(productId, quantity, defaultPers);
        return;
      }
      this.productToAdd(productId);
    },
    productToAdd(productId) {
      const product = this.products.find(p => p.clave === productId);
      if (!product) return;
      const personalizations = product.product_personalizations || [];
      this.actualProductCapturing = {
        product_id: productId,
        personalizations,
        optionPersonalizations: personalizations.map(pers => ({
          text: (pers.personalization && pers.personalization.name) || this.personalizationName(pers.personalization_id),
          image: pers.personalization && pers.personalization.image,
          item: { personalization_id: pers.personalization_id }
        })),
        selectedPersonalizations: personalizations
          .filter(pers => pers.default_selected)
          .map(pers => ({ personalization_id: pers.personalization_id }))
      };
      if (personalizations.length > 0) {
        this.$bvModal.show("modal-productPers");
      } else {
        this.checkExtrasOrFinish();
      }
    },
    persIsSelected(item) {
      return this.actualProductCapturing.selectedPersonalizations.some(
        p => p.personalization_id === item.personalization_id
      );
    },
    togglePersonalization(item) {
      const list = this.actualProductCapturing.selectedPersonalizations;
      const idx = list.findIndex(p => p.personalization_id === item.personalization_id);
      if (idx >= 0) list.splice(idx, 1);
      else list.push({ personalization_id: item.personalization_id });
    },
    persFinishStep() {
      this.$bvModal.hide("modal-productPers");
      this.checkExtrasOrFinish();
    },
    checkExtrasOrFinish() {
      const product = this.products.find(p => p.clave === this.actualProductCapturing.product_id);
      const extraGroups = (product && product.product_extra_groups) || [];
      const activeGroups = extraGroups.filter(g => g.status === 1 && g.items && g.items.length > 0);
      const prevExtras = this.editingExtras || [];
      const masterQty = this.graphqty && this.graphqty > 0 ? this.graphqty : 1;
      this.editingExtras = null;

      this.extrasSelection = [];
      if (activeGroups.length > 0) {
        const areaId = this.cuenta.restaurant_area_id;
        this.extrasSelection = activeGroups.map(group => {
          const activeItems = group.items.filter(i => i.status === 1).sort((a, b) => a.ordenation - b.ordenation);
          const defaultQty =
            activeItems.length === 1
              ? Math.min(Math.max(group.min_selection || 0, 1), group.max_selection || 1)
              : 1;
          const items = activeItems.map(item => {
            const areaPrice = (item.prices || []).find(p => p.restaurant_area_id === areaId);
            const defaultPrice = (item.prices || []).find(p => !p.restaurant_area_id || p.restaurant_area_id === 0);
            const priceObj = areaPrice || defaultPrice;
            const itemProduct = this.products.find(p => p.id === item.product_id);
            const prevExtra = prevExtras.find(e => e.product_id === item.product_id && e.extra_group_id === group.id);
            const prevQty = prevExtra ? Math.round(prevExtra.quantity / masterQty) : 0;
            return {
              id: item.id,
              product_id: item.product_id,
              product_name: itemProduct ? itemProduct.ndescription : "Extra #" + item.product_id,
              price: priceObj ? priceObj.price : 0,
              price_wo_tax: priceObj ? priceObj.price_wo_tax : 0,
              tax: priceObj ? priceObj.tax : 0,
              tax_excempt: priceObj ? priceObj.tax_excempt : 0,
              default_selected: item.default_selected,
              extra_group_id: group.id,
              qty: prevExtras.length > 0 ? prevQty : item.default_selected ? defaultQty : 0
            };
          });
          const isSingle = group.max_selection === 1;
          let selected;
          let radioSelected;
          const source = prevExtras.length > 0 ? items.filter(i => i.qty > 0) : items.filter(i => i.default_selected);
          if (isSingle) {
            selected = source.length > 0 ? [source[0]] : [];
            radioSelected = source.length > 0 ? source[0] : null;
          } else {
            selected = source.slice();
            radioSelected = null;
          }
          return {
            id: group.id,
            name: group.name,
            min_selection: group.min_selection,
            max_selection: group.max_selection,
            items,
            selected,
            radioSelected
          };
        });
      }
      this.$bvModal.show("modal-productConfig");
    },
    groupTotalQty(group) {
      if (group.max_selection === 1) return group.selected.length;
      return group.items.reduce((sum, item) => sum + (item.qty || 0), 0);
    },
    extraQtyChange(group, item, delta) {
      const newQty = (item.qty || 0) + delta;
      if (newQty < 0) return;
      const totalWithout = this.groupTotalQty(group) - (item.qty || 0);
      if (totalWithout + newQty > group.max_selection) return;
      item.qty = newQty;
    },
    configFinish() {
      if (!this.extrasValid) return;
      this.$bvModal.hide("modal-productConfig");

      const masterQty = this.graphqty && this.graphqty > 0 ? this.graphqty : 1;
      this.graphqty = masterQty;
      const before = this.capturingProducts.length;
      this.addProduct(
        this.actualProductCapturing.product_id,
        masterQty,
        this.actualProductCapturing.selectedPersonalizations,
        this.configComment
      );
      if (this.capturingProducts.length === before) {
        // addProduct rechazó el producto (sin precio / inactivo): no agregar extras huérfanos.
        this.extrasSelection = [];
        this.configComment = "";
        return;
      }
      const masterArrId = this.capturingProducts[this.capturingProducts.length - 1].arr_id;
      const pushExtra = (item, qty) => {
        this.capturingProducts.push({
          id_ticket: 0,
          quantity: qty * masterQty,
          is_modifier: false,
          is_extra: true,
          extra_group_id: item.extra_group_id,
          parent_ticket_product_id: null,
          parent_arr_id: masterArrId,
          product_id: item.product_id,
          preparation_comment: "",
          product_name: "  + " + item.product_name,
          price: item.price,
          arr_id: this.capturingProducts.length,
          product_personalizations: []
        });
      };
      for (const group of this.extrasSelection) {
        if (group.max_selection === 1) {
          group.selected.forEach(item => pushExtra(item, 1));
        } else {
          group.items.filter(i => i.qty > 0).forEach(item => pushExtra(item, item.qty));
        }
      }
      this.extrasSelection = [];
      this.configComment = "";
      this.selectTableRow();
    },
    configClose() {
      this.$bvModal.hide("modal-productConfig");
      this.extrasSelection = [];
      this.configComment = "";
      this.editingExtras = null;
    },
    addProduct(productId, quantity, personalizations = [], comment = "") {
      const product = this.products.find(p => p.clave === productId);
      if (!product) {
        this.handleError("danger", "Producto no encontrado", "No encontré el producto buscado: " + productId);
        return;
      }
      if (product.status === "INACTIVE") {
        this.handleError("warning", "Producto DESACTIVADO", "El producto " + product.ndescription + " está DESACTIVADO");
        return;
      }
      const validPrice = (product.product_prices || []).find(
        pr => pr.area_restaurant_id == this.cuenta.restaurant_area_id
      );
      if (!validPrice) {
        this.handleError(
          "danger",
          "Precio no encontrado",
          "No encontré el precio del producto " + product.ndescription + " para el área " +
            this.areaName(this.cuenta.restaurant_area_id) + " en esta sucursal"
        );
        return;
      }
      this.capturingProducts.push({
        id_ticket: 0,
        quantity: parseFloat(quantity),
        is_modifier: false,
        product_id: product.id,
        preparation_comment: comment,
        product_name: product.ndescription,
        price: validPrice.price,
        arr_id: this.capturingProducts.length,
        product_personalizations: personalizations
      });
      this.actualProductCapturing.selectedPersonalizations = [];
      this.selectTableRow();
      this.graphqty = null;
    },
    selectedRow(rows) {
      this.selectedprod = rows[0] || {};
    },
    deleteProduct() {
      if (!this.hasSelection) return;
      const selected = this.selectedprod;
      const masterArrId = selected.is_extra ? selected.parent_arr_id : selected.arr_id;
      this.capturingProducts = this.capturingProducts.filter(
        item => item.arr_id !== masterArrId && item.parent_arr_id !== masterArrId
      );
      this.selectedprod = {};
    },
    editProduct() {
      if (!this.hasSelection) return;
      const selected = this.selectedprod;
      const masterArrId = selected.is_extra ? selected.parent_arr_id : selected.arr_id;
      const master = this.capturingProducts.find(item => item.arr_id === masterArrId);
      if (!master) return;
      const currentExtras = this.capturingProducts.filter(item => item.parent_arr_id === masterArrId);
      const product = this.products.find(p => p.id === master.product_id);
      if (!product) return;

      this.capturingProducts = this.capturingProducts.filter(
        item => item.arr_id !== masterArrId && item.parent_arr_id !== masterArrId
      );
      this.selectedprod = {};
      this.graphqty = master.quantity;
      this.configComment = master.preparation_comment || "";

      const personalizations = product.product_personalizations || [];
      const prevPersIds = (master.product_personalizations || []).map(p => p.personalization_id);
      this.actualProductCapturing = {
        product_id: product.clave,
        personalizations,
        optionPersonalizations: personalizations.map(pers => ({
          text: (pers.personalization && pers.personalization.name) || this.personalizationName(pers.personalization_id),
          image: pers.personalization && pers.personalization.image,
          item: { personalization_id: pers.personalization_id }
        })),
        selectedPersonalizations:
          prevPersIds.length > 0
            ? prevPersIds.map(id => ({ personalization_id: id }))
            : personalizations.filter(p => p.default_selected).map(p => ({ personalization_id: p.personalization_id }))
      };
      this.editingExtras = currentExtras;
      if (personalizations.length > 0) {
        this.$bvModal.show("modal-productPers");
      } else {
        this.checkExtrasOrFinish();
      }
    },
    clearCapture() {
      this.capturingProducts = [];
      this.selectedprod = {};
    },
    captureProducts() {
      if (this.capturingProducts.length < 1) return;
      this.payloadVisible = false;
      this.$bvModal.show("modal-resumen");
    },
    showPayload() {
      this.payloadVisible = true;
      this.$bvModal.show("modal-resumen");
    },
    selectTableRow() {
      setTimeout(() => {
        if (this.$refs.selectableTable) this.$refs.selectableTable.selectRow(this.capturingProducts.length - 1);
      }, 300);
    },
    handleError(variant, title, message) {
      this.flashClass = "flash-" + variant;
      setTimeout(() => (this.flashClass = ""), 1500);
      this.makeToast(variant, title, message);
    },
    makeToast(variant = null, title = "", text = "") {
      this.$bvToast.toast(text, { title, variant, solid: true, appendToast: true });
    }
  }
};
</script>

<style scoped>
.product-menu {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 3;
}
.product-menu >>> .product-menu-toggle {
  padding: 0 4px;
  line-height: 1.4;
  opacity: 0.85;
  border-radius: 50%;
}
.product-card {
  position: relative;
  cursor: pointer;
  transition: box-shadow 0.1s ease-in-out;
}
.product-card:hover {
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.2);
}
.pers-card {
  cursor: pointer;
  position: relative;
  border: 2px solid #dee2e6;
  overflow: hidden;
}
.pers-card.pers-selected {
  border-color: #2dce89;
  box-shadow: 0 0 0 2px rgba(45, 206, 137, 0.25);
}
.pers-check {
  position: absolute;
  top: 4px;
  right: 6px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  line-height: 1;
}
.pers-img {
  width: 100%;
  height: 110px;
  object-fit: cover;
}
.pos-captura {
  transition: background-color 0.3s ease;
  border-radius: 0.25rem;
}
.flash-danger {
  background-color: rgba(220, 53, 69, 0.15);
}
.flash-warning {
  background-color: rgba(255, 193, 7, 0.2);
}
</style>
